import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/topics', label: '话题分类' },
  { to: '/about', label: '关于我们' },
  { to: '/contact', label: '联系我们' },
];

type NavbarProps = {
  brandAsH1?: boolean;
  transparentOnTop?: boolean;
};

export default function Navbar({ brandAsH1 = false, transparentOnTop = false }: NavbarProps) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = !transparentOnTop || scrolled;

  const BrandTag = brandAsH1 ? 'h1' : 'div';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex h-16 md:h-[68px] items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="糖心吃瓜 首页">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg ${
              solid ? 'bg-primary-500 text-background-50' : 'bg-primary-500 text-background-50'
            }`}
          >
            <i className="ri-emotion-laugh-line" aria-hidden="true" />
          </span>
          <BrandTag
            className={`text-xl md:text-2xl leading-none m-0 ${
              solid ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            糖心吃瓜
          </BrandTag>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded-md px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-800 font-medium'
                    : solid
                      ? 'text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
                      : 'text-background-100 hover:bg-background-50/20 hover:text-background-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/topics"
            className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm transition-colors ${
              solid
                ? 'text-foreground-600 hover:text-foreground-950'
                : 'text-background-100 hover:text-background-50'
            }`}
          >
            <i className="ri-search-line" aria-hidden="true" />
            <span>找梗</span>
          </Link>
          <Link
            to="/contact"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            我要投稿
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={open ? '关闭菜单' : '打开菜单'}
          aria-expanded={open}
          className={`md:hidden flex h-10 w-10 items-center justify-center rounded-md text-xl ${
            solid ? 'text-foreground-950 hover:bg-background-200' : 'text-background-50 hover:bg-background-50/20'
          }`}
        >
          <i className={open ? 'ri-close-line' : 'ri-menu-line'} aria-hidden="true" />
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-2">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-4 py-3 text-sm ${
                    isActive
                      ? 'bg-primary-100 text-primary-800 font-medium'
                      : 'text-foreground-700 hover:bg-background-200'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-center text-sm font-medium text-background-50"
            >
              我要投稿
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}